import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, ReferenceArea,
} from "recharts";
import {
  Play, Square, Trophy, Zap, TrendingUp, TrendingDown, Skull, Crown, Eye, EyeOff, AlertTriangle, Rocket, Minus, Plus, Trash2,
  Shield, Activity,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "../components/ui/input";

import { API_BASE as API } from '../config';

function getToken() {
  return localStorage.getItem("token");
}

function headers() {
  return { Authorization: `Bearer ${getToken()}` };
}

const COLORS = ["#FFD700", "#FF3366", "#00E5FF", "#FF6B35", "#B266FF"];
const SCENARIO_COLORS = { A_baseline: "#FFD700", B_pure_taker: "#FF3366", C_hybrid_raw: "#00E5FF", D_hybrid_filtered: "#22C55E" };
const SCENARIO_LABELS = {
  A_baseline: "Baseline (ideal)",
  B_pure_taker: "Pure Taker (worst)",
  C_hybrid_raw: "Hybrid 70/30",
  D_hybrid_filtered: "Hybrid + Filters",
};

const EVENT_ICONS = { crash: AlertTriangle, rally: Rocket, neutral: Minus };

export default function ArenaPage() {
  const [arena, setArena] = useState(null);
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [showEvents, setShowEvents] = useState(true);
  const [macroEvents, setMacroEvents] = useState([]);
  const [eventImpact, setEventImpact] = useState([]);
  const [activePanel, setActivePanel] = useState("rankings"); // rankings | impact | events | validation
  const [taskProgress, setTaskProgress] = useState(null);
  const [customEvents, setCustomEvents] = useState([]);
  const [showEventForm, setShowEventForm] = useState(false);
  const [newEvent, setNewEvent] = useState({ name: "", date: "", event_type: "custom", color: "#FFFFFF" });
  const [validationData, setValidationData] = useState(null);
  const [validationCurves, setValidationCurves] = useState([]);
  const [loadingValidation, setLoadingValidation] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/arena/status`, { headers: headers() });
      setArena(r.data);
      if (r.data.racers?.length > 0) {
        const maxLen = Math.max(...r.data.racers.map(r => r.equity_curve?.length || 0));
        const data = [];
        for (let i = 0; i < maxLen; i += Math.max(1, Math.floor(maxLen / 500))) {
          const point = { candle: i };
          for (const racer of r.data.racers) {
            const ec = racer.equity_curve || [];
            const entry = ec[i] || ec[ec.length - 1];
            if (entry) point[racer.name] = entry.pnl_pct;
          }
          data.push(point);
        }
        setChartData(data);
      }
    } catch (e) { /* ignore */ }
  }, []);

  const fetchMacroEvents = useCallback(async () => {
    try {
      const [evRes, impRes, custRes] = await Promise.all([
        axios.get(`${API}/api/arena/macro-events`, { headers: headers() }),
        axios.get(`${API}/api/arena/event-impact`, { headers: headers() }),
        axios.get(`${API}/api/arena/custom-events`, { headers: headers() }),
      ]);
      setMacroEvents(evRes.data.events || []);
      setEventImpact(impRes.data.impact || []);
      setCustomEvents(custRes.data.events || []);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchStatus();
    const t = setInterval(fetchStatus, 5000);
    return () => clearInterval(t);
  }, [fetchStatus]);

  useEffect(() => {
    if (arena?.has_timestamps && (arena?.phase === "finished" || arena?.racers?.length > 0)) {
      fetchMacroEvents();
    }
  }, [arena?.has_timestamps, arena?.phase, fetchMacroEvents]);

  // Also fetch custom events on mount
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/arena/custom-events`, { headers: headers() });
        setCustomEvents(r.data.events || []);
      } catch (e) { /* ignore */ }
    })();
  }, []);

  // Fetch validation results on mount
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/validate-hybrid/ATR_VOL_m20lb14v15_SL20/results`, { headers: headers() });
        if (r.data && r.data.scenarios) {
          setValidationData(r.data);
          // Build equity curve chart data
          const curves = r.data.equity_curves || {};
          const allKeys = Object.keys(curves);
          if (allKeys.length > 0) {
            const maxLen = Math.max(...allKeys.map(k => (curves[k] || []).length));
            const step = Math.max(1, Math.floor(maxLen / 300));
            const data = [];
            for (let i = 0; i < maxLen; i += step) {
              const pt = { idx: i };
              for (const key of allKeys) {
                const arr = curves[key] || [];
                const entry = arr[Math.min(i, arr.length - 1)];
                if (entry) pt[key] = entry.pnl_pct;
              }
              data.push(pt);
            }
            setValidationCurves(data);
          }
        }
      } catch (e) { /* ignore */ }
    })();
  }, []);

  const addCustomEvent = async () => {
    if (!newEvent.name || !newEvent.date) { toast.error("Name and date required"); return; }
    try {
      await axios.post(`${API}/api/arena/custom-events`, newEvent, { headers: headers() });
      toast.success("Event added!");
      setShowEventForm(false);
      setNewEvent({ name: "", date: "", event_type: "custom", color: "#FFFFFF" });
      fetchMacroEvents();
    } catch (e) { toast.error("Failed to add event"); }
  };

  const deleteCustomEvent = async (eventId) => {
    try {
      await axios.delete(`${API}/api/arena/custom-events/${eventId}`, { headers: headers() });
      toast.success("Event deleted");
      setCustomEvents(prev => prev.filter(e => e.event_id !== eventId));
    } catch (e) { toast.error("Failed to delete"); }
  };

  const startRace = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/api/arena/start?num_racers=10&symbol=ETHUSDT&days=1825&ranking=composite`, {}, { headers: headers() });
      if (r.data.task_id) {
        toast.success(`Arena task launched! Fetching data...`);
        setTaskProgress({ task_id: r.data.task_id, progress: 0, description: "Starting..." });
        // Poll task progress
        const pollTask = setInterval(async () => {
          try {
            const t = await axios.get(`${API}/api/tasks/${r.data.task_id}`, { headers: headers() });
            setTaskProgress({ task_id: r.data.task_id, progress: t.data.progress, description: t.data.description });
            if (t.data.status === "completed" || t.data.status === "failed" || t.data.status === "cancelled") {
              clearInterval(pollTask);
              setTaskProgress(null);
              if (t.data.status === "failed") toast.error(`Race failed: ${t.data.error}`);
            }
          } catch (e) { /* ignore */ }
        }, 1500);
      } else {
        toast.success(`Arena started! ${r.data.racers?.length || 0} racers`);
      }
      setTimeout(fetchStatus, 1000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start arena");
    }
    setLoading(false);
  };

  const stopRace = async () => {
    try {
      await axios.post(`${API}/api/arena/stop`, {}, { headers: headers() });
      toast.info("Arena stopping...");
    } catch (e) { /* ignore */ }
  };

  const racers = (arena?.racers || []).sort((a, b) => a.rank - b.rank);
  const running = arena?.running;
  const phase = arena?.phase || "idle";
  const timer = arena?.timer || {};
  const totalCandles = timer.total_candles || 0;
  const currentCandle = timer.current_candle || 0;
  const progressPct = totalCandles > 0 ? Math.round((currentCandle / (totalCandles - 30)) * 100) : 0;

  return (
    <div className="min-h-screen bg-black text-white p-4 space-y-4" data-testid="arena-page">
      {/* Header */}
      <div className="flex items-center justify-between" data-testid="arena-header">
        <div>
          <h1 className="text-xl font-black tracking-wider flex items-center gap-2">
            <Zap className="w-5 h-5 text-yellow-400" />
            SKYNET STRATEGY ARENA
          </h1>
          <p className="text-xs text-zinc-500 mt-0.5">
            $100K Starting Capital — ETH 4H — Top 10 Composite Strategies — 5 Year Race
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Event Toggle */}
          {macroEvents.length > 0 && (
            <Button
              onClick={() => setShowEvents(!showEvents)}
              variant="outline"
              className={`h-8 px-3 text-xs border-zinc-700 ${showEvents ? "text-amber-400 border-amber-700/50" : "text-zinc-500"}`}
              data-testid="toggle-events-btn"
            >
              {showEvents ? <Eye className="w-3.5 h-3.5 mr-1" /> : <EyeOff className="w-3.5 h-3.5 mr-1" />}
              EVENTS
            </Button>
          )}
          {/* Timer */}
          {running && (
            <div className="flex items-center gap-2 text-sm" data-testid="arena-timer">
              <span className="font-mono text-amber-400">{currentCandle}</span>
              <span className="text-zinc-600">/</span>
              <span className="font-mono text-zinc-500">{totalCandles - 30} candles</span>
              <div className="w-24 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                <div className="h-full bg-amber-500 transition-all" style={{ width: `${progressPct}%` }} />
              </div>
            </div>
          )}
          {phase === "finished" && (
            <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/40" data-testid="arena-finished-badge">RACE COMPLETE</Badge>
          )}
          {!running && (
            <Button onClick={startRace} disabled={loading} className="bg-amber-600 hover:bg-amber-500 text-black font-bold h-8 px-4 text-xs" data-testid="start-arena-btn">
              <Play className="w-3.5 h-3.5 mr-1" /> {phase === "finished" ? "NEW RACE" : "START RACE"}
            </Button>
          )}
          {running && (
            <Button onClick={stopRace} variant="outline" className="border-red-700 text-red-400 h-8 px-3 text-xs" data-testid="stop-arena-btn">
              <Square className="w-3.5 h-3.5 mr-1" /> STOP
            </Button>
          )}
        </div>
      </div>

      {/* Task Progress Banner */}
      {taskProgress && (
        <div className="bg-zinc-900/80 border border-amber-700/40 rounded-sm p-3 flex items-center gap-4" data-testid="task-progress">
          <div className="w-3 h-3 rounded-full bg-amber-500 animate-pulse" />
          <div className="flex-1">
            <div className="text-xs text-amber-400 font-mono mb-1">{taskProgress.description}</div>
            <div className="w-full h-1.5 bg-zinc-800 rounded-full overflow-hidden">
              <div className="h-full bg-amber-500 transition-all duration-500" style={{ width: `${taskProgress.progress}%` }} />
            </div>
          </div>
          <span className="text-xs text-zinc-500 font-mono">{taskProgress.progress}%</span>
        </div>
      )}

      {/* Main Layout: Chart + Side Panel */}
      <div className="grid grid-cols-[1fr_340px] gap-3 h-[calc(100vh-140px)]">
        {/* Chart */}
        <Card className="bg-zinc-950 border-zinc-800 overflow-hidden" data-testid="arena-chart">
          <CardContent className="p-2 h-full relative">
            {racers.length === 0 ? (
              <div className="h-full flex items-center justify-center text-zinc-600 text-sm">
                Click START RACE to begin the arena...
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1a1a2e" />
                  <XAxis dataKey="candle" stroke="#555" tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}`} />
                  <YAxis stroke="#555" tick={{ fontSize: 10 }} tickFormatter={(v) => `${v > 0 ? "+" : ""}${v}%`} domain={["auto", "auto"]} />
                  <ReferenceLine y={0} stroke="#333" strokeWidth={2} />
                  <Tooltip
                    contentStyle={{ background: "#111", border: "1px solid #333", borderRadius: 4, fontSize: 11 }}
                    formatter={(v, name) => [`${v > 0 ? "+" : ""}${v}%`, name]}
                    labelFormatter={(v) => `Candle ${v}`}
                  />
                  {/* Macro Event Markers */}
                  {showEvents && macroEvents.map((ev) => (
                    <React.Fragment key={ev.id}>
                      <ReferenceArea
                        x1={ev.window_start}
                        x2={ev.window_end}
                        fill={ev.color}
                        fillOpacity={0.06}
                      />
                      <ReferenceLine
                        x={ev.candle_index}
                        stroke={ev.color}
                        strokeWidth={1.5}
                        strokeDasharray="4 3"
                        label={{
                          value: ev.name,
                          position: "top",
                          fill: ev.color,
                          fontSize: 9,
                          fontWeight: "bold",
                        }}
                      />
                    </React.Fragment>
                  ))}
                  {racers.map((r) => (
                    <Line
                      key={r.name}
                      type="monotone"
                      dataKey={r.name}
                      stroke={r.color || COLORS[r.id % COLORS.length]}
                      strokeWidth={r.rank === 1 ? 2.5 : 1.5}
                      dot={false}
                      opacity={r.status === "eliminated" ? 0.3 : 1}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            )}
            {racers.length > 0 && (
              <div className="absolute top-4 left-4 text-[10px] text-zinc-600 font-mono">CUMULATIVE P&L (%)</div>
            )}
          </CardContent>
        </Card>

        {/* Side Panel */}
        <div className="flex flex-col gap-2 overflow-hidden" data-testid="arena-side-panel">
          {/* Panel Tabs */}
          <div className="flex gap-1">
            <button
              onClick={() => setActivePanel("rankings")}
              className={`flex-1 text-[10px] font-bold tracking-wider py-1.5 rounded-sm transition-colors ${activePanel === "rankings" ? "bg-amber-600/20 text-amber-400 border border-amber-700/50" : "text-zinc-500 border border-zinc-800 hover:text-zinc-300"}`}
              data-testid="tab-rankings"
            >
              <Trophy className="w-3 h-3 inline mr-1" />RANKS
            </button>
            {eventImpact.length > 0 && (
              <button
                onClick={() => setActivePanel("impact")}
                className={`flex-1 text-[10px] font-bold tracking-wider py-1.5 rounded-sm transition-colors ${activePanel === "impact" ? "bg-red-600/20 text-red-400 border border-red-700/50" : "text-zinc-500 border border-zinc-800 hover:text-zinc-300"}`}
                data-testid="tab-event-impact"
              >
                <AlertTriangle className="w-3 h-3 inline mr-1" />IMPACT
              </button>
            )}
            <button
              onClick={() => setActivePanel("events")}
              className={`flex-1 text-[10px] font-bold tracking-wider py-1.5 rounded-sm transition-colors ${activePanel === "events" ? "bg-blue-600/20 text-blue-400 border border-blue-700/50" : "text-zinc-500 border border-zinc-800 hover:text-zinc-300"}`}
              data-testid="tab-custom-events"
            >
              <Plus className="w-3 h-3 inline mr-1" />EVENTS
            </button>
            <button
              onClick={() => setActivePanel("validation")}
              className={`flex-1 text-[10px] font-bold tracking-wider py-1.5 rounded-sm transition-colors ${activePanel === "validation" ? "bg-emerald-600/20 text-emerald-400 border border-emerald-700/50" : "text-zinc-500 border border-zinc-800 hover:text-zinc-300"}`}
              data-testid="tab-validation"
            >
              <Shield className="w-3 h-3 inline mr-1" />VALID
            </button>
          </div>

          {/* Rankings Panel */}
          {activePanel === "rankings" && (
            <div className="space-y-2 overflow-y-auto flex-1" data-testid="arena-rankings">
              <div className="text-xs font-bold text-zinc-400 tracking-wider pl-1 flex items-center gap-1.5">
                <Trophy className="w-3.5 h-3.5 text-amber-400" /> LIVE RANKINGS
              </div>
              {racers.map((r) => (
                <RacerCard key={r.id} racer={r} isLeader={r.rank === 1} />
              ))}
            </div>
          )}

          {/* Event Impact Panel */}
          {activePanel === "impact" && (
            <div className="space-y-2 overflow-y-auto flex-1" data-testid="event-impact-panel">
              <div className="text-xs font-bold text-zinc-400 tracking-wider pl-1 flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5 text-red-400" /> EVENT IMPACT ANALYSIS
              </div>
              {eventImpact.map((ev) => (
                <EventImpactCard key={ev.event_id} event={ev} />
              ))}
            </div>
          )}

          {/* Custom Events Panel */}
          {activePanel === "events" && (
            <div className="space-y-2 overflow-y-auto flex-1" data-testid="custom-events-panel">
              <div className="flex items-center justify-between mb-1">
                <div className="text-xs font-bold text-zinc-400 tracking-wider pl-1 flex items-center gap-1.5">
                  <Plus className="w-3.5 h-3.5 text-blue-400" /> CUSTOM EVENTS
                </div>
                <Button onClick={() => setShowEventForm(!showEventForm)} variant="outline" className="h-6 px-2 text-[10px] border-zinc-700 text-zinc-400" data-testid="add-event-btn">
                  {showEventForm ? "CANCEL" : "+ ADD"}
                </Button>
              </div>
              {showEventForm && (
                <div className="p-2 border border-zinc-800 rounded-sm bg-zinc-900/60 space-y-2" data-testid="event-form">
                  <Input placeholder="Event name" value={newEvent.name} onChange={(e) => setNewEvent(p => ({...p, name: e.target.value}))} className="h-7 text-xs bg-zinc-900 border-zinc-700" data-testid="event-name-input" />
                  <Input type="date" value={newEvent.date} onChange={(e) => setNewEvent(p => ({...p, date: e.target.value}))} className="h-7 text-xs bg-zinc-900 border-zinc-700" data-testid="event-date-input" />
                  <div className="flex gap-2">
                    <select value={newEvent.event_type} onChange={(e) => setNewEvent(p => ({...p, event_type: e.target.value}))} className="flex-1 h-7 text-xs bg-zinc-900 border border-zinc-700 rounded-sm px-2 text-zinc-300" data-testid="event-type-select">
                      <option value="crash">Crash</option>
                      <option value="rally">Rally</option>
                      <option value="neutral">Neutral</option>
                      <option value="custom">Custom</option>
                    </select>
                    <Input type="color" value={newEvent.color} onChange={(e) => setNewEvent(p => ({...p, color: e.target.value}))} className="h-7 w-12 p-0.5 bg-zinc-900 border-zinc-700" data-testid="event-color-input" />
                  </div>
                  <Button onClick={addCustomEvent} className="w-full h-7 text-xs bg-blue-600 hover:bg-blue-500 text-white" data-testid="save-event-btn">SAVE EVENT</Button>
                </div>
              )}
              {/* List custom events */}
              {customEvents.length === 0 && !showEventForm && (
                <div className="text-zinc-600 text-xs text-center py-4">No custom events. Click + ADD to create one.</div>
              )}
              {customEvents.map((ev) => (
                <div key={ev.event_id} className="p-2 border border-zinc-800/60 rounded-sm bg-zinc-900/40 flex items-center justify-between" data-testid={`custom-event-${ev.event_id}`}>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: ev.color }} />
                    <div>
                      <div className="text-xs text-zinc-300 font-bold">{ev.name}</div>
                      <div className="text-[9px] text-zinc-500 font-mono">{ev.date} &middot; {ev.event_type}</div>
                    </div>
                  </div>
                  <button onClick={() => deleteCustomEvent(ev.event_id)} className="text-zinc-600 hover:text-red-400 transition-colors" data-testid={`delete-event-${ev.event_id}`}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Validation Panel */}
          {activePanel === "validation" && (
            <ValidationPanel data={validationData} curves={validationCurves} data-testid="validation-panel" />
          )}
        </div>
      </div>
    </div>
  );
}


function EventImpactCard({ event }) {
  const Icon = EVENT_ICONS[event.event_type] || Minus;
  const typeColor = event.event_type === "crash" ? "text-red-400" : event.event_type === "rally" ? "text-emerald-400" : "text-zinc-400";
  const sorted = [...event.strategies].sort((a, b) => b.delta_pnl - a.delta_pnl);
  const best = sorted[0];
  const worst = sorted[sorted.length - 1];

  return (
    <div className="p-2.5 rounded-sm border border-zinc-800/60 bg-zinc-900/40" data-testid={`event-card-${event.event_id}`}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: event.color }} />
          <Icon className={`w-3.5 h-3.5 ${typeColor}`} />
          <span className="text-xs font-bold text-zinc-200">{event.event_name}</span>
        </div>
        <span className="text-[9px] text-zinc-500 font-mono">{event.event_date}</span>
      </div>
      {/* Strategy performance during event */}
      <div className="space-y-1">
        {sorted.map((s) => {
          const isPositive = s.delta_pnl >= 0;
          const isBest = s.name === best?.name;
          const isWorst = s.name === worst?.name && sorted.length > 1;
          return (
            <div key={s.name} className={`flex items-center justify-between text-[10px] py-0.5 px-1 rounded-sm ${isBest ? "bg-emerald-950/30" : isWorst ? "bg-red-950/30" : ""}`}>
              <div className="flex items-center gap-1.5 truncate max-w-[160px]">
                <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
                <span className="text-zinc-400 truncate">{s.name}</span>
              </div>
              <span className={`font-mono font-bold ${isPositive ? "text-emerald-400" : "text-red-400"}`}>
                {isPositive ? "+" : ""}{s.delta_pnl}%
              </span>
            </div>
          );
        })}
      </div>
      {/* Summary */}
      <div className="mt-1.5 pt-1 border-t border-zinc-800/50 flex justify-between text-[9px] text-zinc-500 font-mono">
        <span>Best: <span className="text-emerald-400">{best?.name?.substring(0, 12)}</span></span>
        <span>Worst: <span className="text-red-400">{worst?.name?.substring(0, 12)}</span></span>
      </div>
    </div>
  );
}


function RacerCard({ racer, isLeader }) {
  const isPositive = racer.pnl_pct >= 0;
  const isEliminated = racer.status === "eliminated";
  const yearly = racer.yearly_returns || {};
  const yearEntries = Object.entries(yearly);

  return (
    <div
      className={`p-2.5 rounded-sm border transition-all ${
        isLeader ? "bg-amber-950/20 border-amber-700/50" :
        isEliminated ? "bg-red-950/10 border-red-900/30 opacity-50" :
        "bg-zinc-900/60 border-zinc-800/60"
      }`}
      data-testid={`racer-card-${racer.id}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={`text-sm font-black w-5 ${isLeader ? "text-amber-400" : "text-zinc-600"}`}>
            {isLeader && <Crown className="w-4 h-4 inline" />}
            {!isLeader && `#${racer.rank}`}
          </span>
          <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: racer.color }} />
          <span className="text-xs font-bold text-zinc-300 truncate max-w-[130px]">{racer.name}</span>
        </div>
        {isEliminated && <Skull className="w-3.5 h-3.5 text-red-500" />}
      </div>
      <div className="flex items-center justify-between mt-1.5">
        <span className={`text-lg font-mono font-black ${isPositive ? "text-emerald-400" : "text-red-400"}`}>
          {isPositive ? "+" : ""}{racer.pnl_pct.toFixed(2)}%
        </span>
        <div className="flex items-center gap-1 text-[10px] text-zinc-500">
          {isPositive ? <TrendingUp className="w-3 h-3 text-emerald-500" /> : <TrendingDown className="w-3 h-3 text-red-500" />}
          {racer.trades}T / {racer.win_rate}% WR
        </div>
      </div>
      <div className="mt-1 flex gap-2 text-[9px] font-mono text-zinc-500">
        {racer.sharpe ? <span>Sh: <span className="text-cyan-400">{racer.sharpe}</span></span> : null}
        {racer.max_drawdown_pct ? <span>DD: <span className="text-orange-400">{racer.max_drawdown_pct}%</span></span> : null}
        {racer.profit_factor ? <span>PF: <span className="text-amber-400">{racer.profit_factor}</span></span> : null}
      </div>
      {racer.avg_annual_pnl !== undefined && racer.avg_annual_pnl !== 0 && (
        <div className="mt-1 text-[10px] text-cyan-400 font-mono">
          Avg/Year: {racer.avg_annual_pnl > 0 ? "+" : ""}{racer.avg_annual_pnl}%
        </div>
      )}
      {yearEntries.length > 1 && (
        <div className="mt-1.5 flex flex-wrap gap-x-2 gap-y-0.5">
          {yearEntries.map(([yr, pnl]) => (
            <span key={yr} className={`text-[9px] font-mono ${pnl >= 0 ? "text-emerald-500/70" : "text-red-400/70"}`}>
              {yr}: {pnl > 0 ? "+" : ""}{pnl}%
            </span>
          ))}
        </div>
      )}
    </div>
  );
}


function ValidationPanel({ data, curves }) {
  if (!data || !data.scenarios) {
    return (
      <div className="flex-1 flex items-center justify-center text-zinc-600 text-xs" data-testid="validation-panel">
        <div className="text-center space-y-2">
          <Shield className="w-8 h-8 mx-auto text-zinc-700" />
          <p>No validation data yet.</p>
          <p className="text-[10px] text-zinc-700">Run hybrid validation via API first.</p>
        </div>
      </div>
    );
  }

  const verdict = data.verdict || {};
  const scenarios = data.scenarios || {};
  const config = data.config || {};
  const lightColor = verdict.light === "GREEN" ? "text-emerald-400" : verdict.light === "YELLOW" ? "text-amber-400" : "text-red-400";
  const lightBg = verdict.light === "GREEN" ? "bg-emerald-950/30 border-emerald-700/50" : verdict.light === "YELLOW" ? "bg-amber-950/30 border-amber-700/50" : "bg-red-950/30 border-red-700/50";

  return (
    <div className="space-y-2 overflow-y-auto flex-1" data-testid="validation-panel">
      {/* Verdict Badge */}
      <div className={`p-2.5 rounded-sm border ${lightBg}`} data-testid="validation-verdict">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className={`w-4 h-4 ${lightColor}`} />
            <span className={`text-sm font-black tracking-wider ${lightColor}`}>{verdict.light}</span>
          </div>
          <span className="text-[9px] text-zinc-500 font-mono">{data.candles} candles</span>
        </div>
        <p className="text-[10px] text-zinc-400 mt-1">{verdict.action}</p>
      </div>

      {/* Target vs Actual */}
      <div className="p-2 border border-zinc-800/60 rounded-sm bg-zinc-900/40" data-testid="validation-targets">
        <div className="text-[10px] font-bold text-zinc-500 tracking-wider mb-1.5">TARGET vs ACTUAL</div>
        {Object.entries(verdict.target_vs_actual || {}).map(([metric, vals]) => {
          const pass = (metric === "sharpe" && vals.actual >= 2.0) ||
                       (metric === "profit_factor" && vals.actual >= 1.6) ||
                       (metric === "max_drawdown_pct" && vals.actual <= 25);
          return (
            <div key={metric} className="flex items-center justify-between py-0.5">
              <span className="text-[10px] text-zinc-400">{metric.replace(/_/g, " ")}</span>
              <div className="flex items-center gap-2">
                <span className="text-[9px] text-zinc-600 font-mono">{vals.target}</span>
                <span className={`text-[10px] font-mono font-bold ${pass ? "text-emerald-400" : "text-red-400"}`}>{vals.actual}</span>
              </div>
            </div>
          );
        })}
        <div className="flex items-center justify-between pt-1 mt-1 border-t border-zinc-800/50">
          <span className="text-[10px] text-zinc-500">Degradation</span>
          <span className="text-[10px] font-mono text-amber-400">{verdict.degradation_from_baseline_pct}%</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-zinc-500">Trade reduction</span>
          <span className="text-[10px] font-mono text-cyan-400">{verdict.trade_reduction_pct}%</span>
        </div>
      </div>

      {/* Equity Curve Mini Chart */}
      {curves.length > 0 && (
        <div className="border border-zinc-800/60 rounded-sm bg-zinc-900/40 p-1" data-testid="validation-chart">
          <div className="text-[9px] font-bold text-zinc-500 tracking-wider px-1 mb-0.5">EQUITY CURVES</div>
          <ResponsiveContainer width="100%" height={120}>
            <LineChart data={curves} margin={{ top: 2, right: 4, left: 4, bottom: 2 }}>
              <CartesianGrid strokeDasharray="2 2" stroke="#1a1a2e" />
              <XAxis dataKey="idx" hide />
              <YAxis tick={{ fontSize: 8 }} stroke="#333" tickFormatter={v => `${v > 0 ? "+" : ""}${v}%`} width={35} />
              <Tooltip
                contentStyle={{ background: "#111", border: "1px solid #333", borderRadius: 4, fontSize: 9 }}
                formatter={(v, name) => [`${(+v).toFixed(1)}%`, SCENARIO_LABELS[name] || name]}
                labelFormatter={() => ""}
              />
              {Object.keys(SCENARIO_COLORS).map(key => (
                <Line key={key} type="monotone" dataKey={key} stroke={SCENARIO_COLORS[key]} strokeWidth={key === "D_hybrid_filtered" ? 2 : 1} dot={false} opacity={key === "A_baseline" ? 0.4 : 1} />
              ))}
            </LineChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap gap-x-3 gap-y-0.5 px-1 mt-0.5">
            {Object.entries(SCENARIO_LABELS).map(([key, label]) => (
              <div key={key} className="flex items-center gap-1">
                <div className="w-2 h-0.5 rounded-full" style={{ backgroundColor: SCENARIO_COLORS[key] }} />
                <span className="text-[8px] text-zinc-500">{label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Scenario Cards */}
      {Object.entries(scenarios).map(([name, s]) => {
        const label = SCENARIO_LABELS[name] || name;
        const color = SCENARIO_COLORS[name] || "#888";
        const ex = s.execution_stats || {};
        return (
          <div key={name} className="p-2 border border-zinc-800/60 rounded-sm bg-zinc-900/40" data-testid={`scenario-${name}`}>
            <div className="flex items-center gap-1.5 mb-1">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
              <span className="text-[10px] font-bold text-zinc-300">{label}</span>
            </div>
            <div className="grid grid-cols-4 gap-1 text-center">
              <div>
                <div className="text-[8px] text-zinc-600">Return</div>
                <div className={`text-[10px] font-mono font-bold ${s.total_return_pct >= 0 ? "text-emerald-400" : "text-red-400"}`}>{s.total_return_pct?.toFixed(1)}%</div>
              </div>
              <div>
                <div className="text-[8px] text-zinc-600">Sharpe</div>
                <div className="text-[10px] font-mono font-bold text-cyan-400">{s.sharpe?.toFixed(2)}</div>
              </div>
              <div>
                <div className="text-[8px] text-zinc-600">PF</div>
                <div className="text-[10px] font-mono font-bold text-amber-400">{s.profit_factor?.toFixed(2)}</div>
              </div>
              <div>
                <div className="text-[8px] text-zinc-600">MaxDD</div>
                <div className="text-[10px] font-mono font-bold text-orange-400">{s.max_drawdown_pct?.toFixed(1)}%</div>
              </div>
            </div>
            <div className="flex gap-2 mt-1 text-[9px] text-zinc-500 font-mono">
              <span>{s.trades}T</span>
              <span>{s.win_rate}% WR</span>
              {ex.actual_maker_rate !== undefined && <span>M:{ex.actual_maker_rate}%</span>}
              {ex.filtered_out > 0 && <span className="text-amber-500">F:{ex.filtered_out}</span>}
            </div>
          </div>
        );
      })}

      {/* Config Info */}
      <div className="p-2 border border-zinc-800/40 rounded-sm bg-zinc-950/40" data-testid="validation-config">
        <div className="text-[9px] font-bold text-zinc-600 tracking-wider mb-1">EXECUTION CONFIG</div>
        {Object.entries(config).map(([k, v]) => (
          <div key={k} className="flex items-center justify-between text-[9px]">
            <span className="text-zinc-600">{k.replace(/_/g, " ")}</span>
            <span className="text-zinc-400 font-mono">{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
